import { useEffect, useState } from "react";
import api from "../constant/api";
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  DollarSign, 
  Calendar, 
  RotateCcw, 
  Sparkles, 
  CreditCard,
  Wallet,
  Building2,
  Share2,
  ShieldCheck,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

export default function GarmentsDashboard() {
  const [stats, setStats] = useState({
    summary: { 
      today_sales: 0, 
      today_cash_sales: 0, 
      today_upi_sales: 0, 
      today_card_sales: 0, 
      today_expense: 0, 
      cashBalance: 0, 
      monthly_sales: 0, 
      monthly_expense: 0,
      yearly_sales: 0,
      yearly_expense: 0 
    },
    highestSales: [],
    lowestSales: [],
    lowStock: [],
    returnsStats: { total_returns: 0, total_return_qty: 0 }
  });

  const selectedBranch = localStorage.getItem("selectedBranch") || "2";
  const selectedBranchName = localStorage.getItem("selectedBranchName") || "Ak Ceramic World";

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await api.get(`/GarmentsProduct/getAnalytics?branch=${selectedBranch}`);
      if (res.data?.success) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Dashboard error:", err);
    }
  };

  const sendWhatsAppReport = () => {
    const today = new Date().toLocaleDateString("en-IN");
    const s = stats.summary;

    const message = `📊 *DAILY GARMENTS SALES & EXPENSE REPORT* 📊\n` +
      `📅 *Date:* ${today}\n` +
      `🏢 *Branch:* ${selectedBranchName}\n` +
      `-----------------------------------\n` +
      `💰 *Today Total Sales:* ₹${Number(s.today_sales || 0).toLocaleString("en-IN")}\n` +
      `💵 *Cash Sales:* ₹${Number(s.today_cash_sales || 0).toLocaleString("en-IN")}\n` +
      `📱 *UPI Sales (Bank):* ₹${Number(s.today_upi_sales || 0).toLocaleString("en-IN")}\n` +
      `💳 *Card Sales (Bank):* ₹${Number(s.today_card_sales || 0).toLocaleString("en-IN")}\n` +
      `-----------------------------------\n` +
      `🔻 *Today Expense:* ₹${Number(s.today_expense || 0).toLocaleString("en-IN")}\n` +
      `-----------------------------------\n` +
      `✅ *Net Cash Balance in Hand:* ₹${Number(s.cashBalance || 0).toLocaleString("en-IN")}\n` +
      `-----------------------------------\n` +
      `Generated from Garments POS System.`;

    const encodedMsg = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMsg}`, "_blank");
  };

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-900 min-h-screen text-slate-100 font-sans">
      
      {/* HEADER BANNER */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 shadow-2xl border border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-amber-300 animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest text-indigo-200">Garments Executive Analytics</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white">{selectedBranchName}</h1>
          <p className="text-xs text-indigo-100/80 mt-1">Full Business Overview: Today, Monthly, Yearly Sales, Expenses, Stocks & Returns</p>
        </div>

        <button
          onClick={sendWhatsAppReport}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-2.5 rounded-2xl text-xs shadow-lg flex items-center gap-2 transition"
        >
          <Share2 size={16} /> Share Report on WhatsApp
        </button>
      </div>

      {/* TODAY's METRICS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <div className="flex justify-between items-center text-slate-400 border-b border-slate-700/50 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Today Total Sales</span>
            <DollarSign size={18} className="text-emerald-400" />
          </div>
          <h2 className="text-2xl font-black text-emerald-400">₹{Number(stats.summary.today_sales || 0).toLocaleString("en-IN")}</h2>
          <p className="text-[11px] text-slate-400">Gross collection for today</p>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <div className="flex justify-between items-center text-slate-400 border-b border-slate-700/50 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-rose-400">Today Approved Expense</span>
            <ArrowDownRight size={18} className="text-rose-400" />
          </div>
          <h2 className="text-2xl font-black text-rose-400">₹{Number(stats.summary.today_expense || 0).toLocaleString("en-IN")}</h2>
          <p className="text-[11px] text-slate-400">Total shop operational expense today</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-900/60 to-purple-900/60 backdrop-blur-xl p-5 rounded-3xl border border-indigo-500/40 shadow-xl space-y-3">
          <div className="flex justify-between items-center text-slate-300 border-b border-indigo-500/30 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Net Cash Balance In Hand</span>
            <Wallet size={18} className="text-amber-300" />
          </div>
          <h2 className="text-2xl font-black text-amber-300">₹{Number(stats.summary.cashBalance || 0).toLocaleString("en-IN")}</h2>
          <p className="text-[11px] text-indigo-200">(Cash Sales - Today Expense)</p>
        </div>
      </div>

      {/* PAYMENT MODE BREAKDOWN */}
      <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-200 border-b border-slate-700/50 pb-2 flex items-center gap-2">
          <CreditCard size={16} className="text-indigo-400" /> Today Collection Mode Breakdown
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-900/60 border border-slate-700/60 p-4 rounded-2xl flex justify-between items-center">
            <div>
              <p className="text-[11px] text-slate-400 font-bold uppercase">Cash Sales</p>
              <h3 className="text-lg font-bold text-slate-100 mt-1">₹{Number(stats.summary.today_cash_sales || 0).toLocaleString("en-IN")}</h3>
            </div>
            <Wallet className="text-emerald-400" size={24} />
          </div>

          <div className="bg-slate-900/60 border border-slate-700/60 p-4 rounded-2xl flex justify-between items-center">
            <div>
              <p className="text-[11px] text-slate-400 font-bold uppercase">UPI Sales (Bank)</p>
              <h3 className="text-lg font-bold text-slate-100 mt-1">₹{Number(stats.summary.today_upi_sales || 0).toLocaleString("en-IN")}</h3>
            </div>
            <Building2 className="text-indigo-400" size={24} />
          </div>

          <div className="bg-slate-900/60 border border-slate-700/60 p-4 rounded-2xl flex justify-between items-center">
            <div>
              <p className="text-[11px] text-slate-400 font-bold uppercase">Card Sales (Bank)</p>
              <h3 className="text-lg font-bold text-slate-100 mt-1">₹{Number(stats.summary.today_card_sales || 0).toLocaleString("en-IN")}</h3>
            </div>
            <CreditCard className="text-amber-400" size={24} />
          </div>
        </div>
      </div>

      {/* MONTHLY & YEARLY FINANCIAL COMPARISON */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Monthly */}
        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <div className="flex justify-between items-center text-slate-400 border-b border-slate-700/50 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-300">Monthly Financial Overview</span>
            <Calendar size={18} className="text-indigo-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-400">Total Monthly Sales:</span>
              <span className="text-lg font-extrabold text-emerald-400">₹{Number(stats.summary.monthly_sales || 0).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-400">Total Monthly Expense:</span>
              <span className="text-sm font-bold text-rose-400">₹{Number(stats.summary.monthly_expense || 0).toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>

        {/* Yearly */}
        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <div className="flex justify-between items-center text-slate-400 border-b border-slate-700/50 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-300">Yearly Financial Overview</span>
            <TrendingUp size={18} className="text-amber-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-400">Total Yearly Sales:</span>
              <span className="text-lg font-extrabold text-emerald-400">₹{Number(stats.summary.yearly_sales || 0).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-400">Total Yearly Expense:</span>
              <span className="text-sm font-bold text-rose-400">₹{Number(stats.summary.yearly_expense || 0).toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* HIGHEST & LOWEST SELLING PRODUCTS */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <h2 className="text-xs font-bold uppercase text-slate-200 flex items-center gap-2 border-b border-slate-700/50 pb-2">
            <TrendingUp size={16} className="text-emerald-400" /> Highest Selling Garment Variants
          </h2>
          <div className="divide-y divide-slate-700/40 text-xs max-h-52 overflow-y-auto">
            {stats.highestSales.map((item, idx) => (
              <div key={idx} className="py-2.5 flex justify-between items-center">
                <div>
                  <p className="font-bold text-slate-100">{item.product_name}</p>
                  <p className="text-[11px] text-slate-400">{item.size} / {item.color}</p>
                </div>
                <span className="text-emerald-400 font-bold">{item.total_qty} Sold Pcs</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <h2 className="text-xs font-bold uppercase text-slate-200 flex items-center gap-2 border-b border-slate-700/50 pb-2">
            <TrendingDown size={16} className="text-rose-400" /> Lowest Selling Garment Variants
          </h2>
          <div className="divide-y divide-slate-700/40 text-xs max-h-52 overflow-y-auto">
            {stats.lowestSales.map((item, idx) => (
              <div key={idx} className="py-2.5 flex justify-between items-center">
                <div>
                  <p className="font-bold text-slate-100">{item.product_name}</p>
                  <p className="text-[11px] text-slate-400">{item.size} / {item.color}</p>
                </div>
                <span className="text-rose-400 font-bold">{item.total_qty} Sold Pcs</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* LOW STOCK & RETURNS */}
      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3">
          <h2 className="text-xs font-bold uppercase text-slate-200 flex items-center gap-2 border-b border-slate-700/50 pb-2">
            <AlertTriangle size={16} className="text-amber-400" /> Low Stock Variants Alert
          </h2>
          <div className="divide-y divide-slate-700/40 text-xs max-h-52 overflow-y-auto">
            {stats.lowStock.map((prod, idx) => (
              <div key={idx} className="py-2.5 flex justify-between items-center">
                <div>
                  <p className="font-bold text-slate-100">{prod.product_name}</p>
                  <p className="text-[11px] text-slate-400">{prod.size} / {prod.color}</p>
                </div>
                <span className="bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-lg font-bold">
                  {prod.qty} pcs left
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-xl p-5 rounded-3xl border border-slate-700/60 shadow-xl space-y-3 flex flex-col justify-between">
          <h2 className="text-xs font-bold uppercase text-slate-200 flex items-center gap-2 border-b border-slate-700/50 pb-2">
            <RotateCcw size={16} className="text-purple-400" /> Returns Summary
          </h2>
          <div className="space-y-3 my-auto text-center">
            <div className="bg-slate-900/60 border border-slate-700/60 p-3 rounded-2xl">
              <p className="text-[11px] text-slate-400 font-bold uppercase">Total Return Requests</p>
              <h3 className="text-xl font-bold text-amber-400 mt-0.5">{stats.returnsStats.total_returns}</h3>
            </div>
            <div className="bg-slate-900/60 border border-slate-700/60 p-3 rounded-2xl">
              <p className="text-[11px] text-slate-400 font-bold uppercase">Returned Quantities</p>
              <h3 className="text-xl font-bold text-rose-400 mt-0.5">{stats.returnsStats.total_return_qty} Pcs</h3>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}